// src/components/WhatsAppButton.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { X, MessageCircle } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const WhatsAppButton = () => {
  const [config, setConfig] = useState(null);
  const [showBubble, setShowBubble] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'whatsapp');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching WhatsApp config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Hide bubble after 8 seconds
  useEffect(() => {
    if (!showBubble) return;
    const timer = setTimeout(() => setShowBubble(false), 8000);
    return () => clearTimeout(timer);
  }, [showBubble]);

  if (loading || !config || config.active === false || !config.phone) return null;

  // Clean phone number (remove spaces, dashes, etc.)
  const cleanPhone = config.phone.replace(/[^0-9]/g, '');
  const message = encodeURIComponent(config.message || 'Hello! I need help.');
  const whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`;

  const position = config.position || 'bottom-right';
  const positionClasses = {
    'bottom-right': 'bottom-24 right-4 md:bottom-6 md:right-6',
    'bottom-left': 'bottom-24 left-4 md:bottom-6 md:left-6',
  };

  const bubblePosition = {
    'bottom-right': 'right-16 md:right-20',
    'bottom-left': 'left-16 md:left-20',
  };

  return (
    <div className={`fixed z-50 ${positionClasses[position]} anim-fadeIn`}>

      {/* Message Bubble */}
      {showBubble && config.bubbleText && (
        <div
          className={`absolute bottom-2 ${bubblePosition[position]} bg-white rounded-2xl shadow-2xl p-3 pr-8 max-w-[220px] anim-slideUp`}
          style={{ border: '1px solid var(--color-border)' }}
        >
          <button
            onClick={() => setShowBubble(false)}
            className="absolute top-1.5 right-1.5 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={14} />
          </button>

          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'rgba(37, 211, 102, 0.15)' }}>
              <MessageCircle size={16} style={{ color: '#25D366' }} />
            </div>
            <div>
              <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--color-primary-text)' }}>
                Need help?
              </p>
              <p className="text-[11px] leading-tight" style={{ color: 'var(--color-secondary-text)' }}>
                {config.bubbleText}
              </p>
            </div>
          </div>

          {/* Arrow */}
          <div
            className={`absolute bottom-4 ${position === 'bottom-right' ? '-right-1.5' : '-left-1.5'} w-3 h-3 bg-white rotate-45`}
            style={{ borderRight: '1px solid var(--color-border)', borderTop: '1px solid var(--color-border)' }}
          ></div>
        </div>
      )}

      {/* WhatsApp Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="relative group"
        aria-label="Chat on WhatsApp"
      >
        {/* Pulse Ring */}
        <span className="absolute inset-0 rounded-full anim-pulseGlow"
          style={{ backgroundColor: '#25D366', opacity: 0.4 }}></span>

        {/* Button */}
        <div
          className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110 btn-2d"
          style={{ backgroundColor: '#25D366' }}
        >
          <FaWhatsapp size={32} color="#ffffff" className="md:w-9 md:h-9" />
        </div>
      </a>

      {/* Inline Animation */}
      <style>{`
        @keyframes pulse-glow {
          0% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.3); opacity: 0.1; }
          100% { transform: scale(1); opacity: 0.4; }
        }
        .anim-pulseGlow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default WhatsAppButton;